/**
 * Core domain vocabulary shared by the API, engine, portal and mobile app.
 * Every string here is persisted, so values are stable identifiers: rename the
 * label, never the value.
 */

export const ROLES = ['supervisor', 'payroll_admin', 'owner'] as const;
export type Role = (typeof ROLES)[number];

/** Higher number = more privilege. Used for "at least" checks. */
export const ROLE_RANK: Record<Role, number> = { supervisor: 1, payroll_admin: 2, owner: 3 };

export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'casual'] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYEE_STATUSES = ['active', 'suspended', 'terminated'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const EVENT_TYPES = ['clock_in', 'clock_out', 'break_start', 'break_end', 'job_switch'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_SOURCES = ['mobile', 'kiosk', 'manual', 'system'] as const;
export type EventSource = (typeof EVENT_SOURCES)[number];

export const EVENT_STATUSES = ['active', 'superseded', 'voided'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const BREAK_KINDS = ['meal', 'rest'] as const;
export type BreakKind = (typeof BREAK_KINDS)[number];

export const REASON_CODES = [
  'missed_punch',
  'device_fault',
  'wrong_time',
  'duplicate',
  'roster_error',
  'wrong_site',
  'auto_clock_out',
  'other',
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

export const REASON_LABELS: Record<ReasonCode, string> = {
  missed_punch: 'Missed punch',
  device_fault: 'Device fault',
  wrong_time: 'Wrong time',
  duplicate: 'Duplicate',
  roster_error: 'Roster error',
  wrong_site: 'Wrong site',
  auto_clock_out: 'Auto clock-out',
  other: 'Other',
};

export const EXCEPTION_SEVERITIES = ['blocking', 'review', 'info'] as const;
export type ExceptionSeverity = (typeof EXCEPTION_SEVERITIES)[number];

export const EXCEPTION_TYPES = [
  'missed_punch',
  'outside_geofence',
  'no_location',
  'late_start',
  'early_start',
  'early_finish',
  'absent',
  'unrostered_work',
  'wrong_site',
  'long_shift',
  'auto_clock_out',
  'missed_meal_break',
  'short_meal_break',
  'duplicate_punch',
  'clock_skew',
  'orphan_job_switch',
  'no_rule_set',
] as const;
export type ExceptionType = (typeof EXCEPTION_TYPES)[number];

export const EXCEPTION_LABELS: Record<ExceptionType, string> = {
  missed_punch: 'Missed punch',
  outside_geofence: 'Outside geofence',
  no_location: 'No location',
  late_start: 'Late start',
  early_start: 'Early start',
  early_finish: 'Early finish',
  absent: 'Rostered but absent',
  unrostered_work: 'Unrostered work',
  wrong_site: 'Wrong site',
  long_shift: 'Long shift',
  auto_clock_out: 'Auto clock-out',
  missed_meal_break: 'Missed meal break',
  short_meal_break: 'Short meal break',
  duplicate_punch: 'Duplicate punch',
  clock_skew: 'Device clock skew',
  orphan_job_switch: 'Job switch outside a shift',
  no_rule_set: 'No pay rules assigned',
};

/** Exceptions that can only be cleared by a correction (never dismissed). */
export const CORRECTION_ONLY_EXCEPTIONS: readonly ExceptionType[] = ['missed_punch', 'auto_clock_out', 'no_rule_set'];

export const EXCEPTION_STATUSES = ['open', 'resolved', 'dismissed'] as const;
export type ExceptionStatus = (typeof EXCEPTION_STATUSES)[number];

export const TIMESHEET_STATUSES = [
  'open',
  'ready',
  'needs_attention',
  'approved',
  'rejected',
  'locked',
  'exported',
] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];

export const PERIOD_STATUSES = ['open', 'locked', 'exported'] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];

export const PAY_PERIOD_TYPES = ['weekly', 'fortnightly'] as const;
export type PayPeriodType = (typeof PAY_PERIOD_TYPES)[number];

export const CORRECTION_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type CorrectionStatus = (typeof CORRECTION_STATUSES)[number];

export const DEVICE_KINDS = ['mobile', 'kiosk'] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

export const DEVICE_STATUSES = ['pending', 'active', 'revoked'] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

export const EXPORT_METHODS = ['api', 'file'] as const;
export type ExportMethod = (typeof EXPORT_METHODS)[number];

export const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'] as const;
export type AuState = (typeof AU_STATES)[number];

export const AU_TIMEZONES: Record<AuState, string> = {
  NSW: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  WA: 'Australia/Perth',
  TAS: 'Australia/Hobart',
  ACT: 'Australia/Sydney',
  NT: 'Australia/Darwin',
};

/**
 * Payroll category codes the engine emits. Each code maps to a MYOB payroll
 * category UID in settings. The engine never applies a pay rate: MYOB does.
 */
export const DEFAULT_CATEGORIES = [
  { code: 'ORD', name: 'Ordinary hours', unit: 'hours' },
  { code: 'OT1', name: 'Overtime tier 1', unit: 'hours' },
  { code: 'OT2', name: 'Overtime tier 2', unit: 'hours' },
  { code: 'SAT', name: 'Saturday', unit: 'hours' },
  { code: 'SUN', name: 'Sunday', unit: 'hours' },
  { code: 'PH', name: 'Public holiday', unit: 'hours' },
  { code: 'ALLOW_MEAL', name: 'Meal allowance', unit: 'units' },
  { code: 'ALLOW_TOOL', name: 'Tool allowance', unit: 'units' },
  { code: 'ALLOW_TRAVEL', name: 'Travel allowance', unit: 'units' },
] as const;

/** Event-type transitions allowed from each punch state, used by app and kiosk. */
export type PunchState = 'out' | 'in' | 'on_break';
export function allowedActions(state: PunchState): EventType[] {
  switch (state) {
    case 'out':
      return ['clock_in'];
    case 'in':
      return ['break_start', 'clock_out', 'job_switch'];
    case 'on_break':
      return ['break_end', 'clock_out'];
  }
}
export function nextState(state: PunchState, type: EventType): PunchState {
  if (type === 'clock_in') return 'in';
  if (type === 'clock_out') return 'out';
  if (type === 'break_start') return 'on_break';
  if (type === 'break_end') return 'in';
  return state;
}
export const EVENT_LABELS: Record<EventType, string> = {
  clock_in: 'Clock in',
  clock_out: 'Clock out',
  break_start: 'Start break',
  break_end: 'End break',
  job_switch: 'Switch job',
};
